import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, List, Select, TextInput } from '@mantine/core';
import { ChevronRight } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { Context as SportsheadzWebContext } from '../../../providers/SportsheadzWebProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { formatUrl } from '../../../helpers/format';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import PaginationList from '../../common/PaginationList';
import classes from '../../../styles/nestedStyles.module.css';
import { triggerNotification } from '../../../helpers/notificationHelper';

const TeamRebaseConnectionModal = ({
  isOpen,
  onClose,
  regTeam,
  regAssociation
}) => {
  const {
    state: regState,
    fetchRegAssociationDivisionTeams,
    rebaseRegAssociationDivisionTeamConnection
  } = useContext(RegistrationAdminContext);
  const { state, fetchAssociationTeams, fetchAssociationSeasons } = useContext(
    SportsheadzWebContext
  );
  const [formState, setFormState] = useState({
    teamSearch: '',
    fkSeason: null,
    team: null,
    loading: false
  });

  const associationUrl = regAssociation
    ? formatUrl(regAssociation.association.domain)
    : '';

  const dataLoading =
    state.teams.loading ||
    state.seasons.loading ||
    state.teams.filter?.associationDomain !== associationUrl;

  const associationSeasonTeams = state.teams.value
    .filter(
      (t) =>
        t.App_ID &&
        (!formState.fkSeason || t.SeasonID.toString() === formState.fkSeason) &&
        t.FullName.toLowerCase().includes(formState.teamSearch.toLowerCase())
    )
    .sort((a, b) => a.Name.localeCompare(b.Name));

  const alreadyImportedTeams = formState.team
    ? regState.regAssociationDivisionTeamsSummary.value.filter(
        (t) =>
          formState.team.App_ID === t.fkTeam &&
          t.pkRegAssociationDivisionTeam !==
            regTeam.pkRegAssociationDivisionTeam
      )
    : [];

  useEffect(() => {
    if (isOpen && regAssociation) {
      setFormState({
        teamSearch: '',
        fkSeason: null,
        team: null,
        loading: false
      });

      if (state.teams.filter?.associationDomain !== associationUrl) {
        fetchAssociationTeams(associationUrl);
      }

      if (state.seasons.filter?.associationDomain !== associationUrl) {
        fetchAssociationSeasons(associationUrl);
      }

      fetchRegAssociationDivisionTeams({
        pkRegAssociation: regAssociation.pkRegAssociation,
        summaryOnly: true
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      centered
      formSectionProps={{
        onCancel: () => {
          if (formState.team) {
            setFormState({
              ...formState,
              team: null
            });
          }
          else {
            onClose();
          }
        },
        cancelTitle: formState.team ? 'Back' : 'Cancel',
        isSubmitHidden: !formState.team,
        isLoading: formState.loading,
        submitTitle: 'Yes, rebase connection',
        onSubmit: () => {
          setFormState({
            ...formState,
            loading: true
          });

          rebaseRegAssociationDivisionTeamConnection(
            regTeam.pkRegAssociationDivisionTeam,
            { pkTeam: formState.team.App_ID },
            () => {
              triggerNotification('Team connection updated', 'success');
              onClose();
            },
            () => {
              triggerNotification(
                'Failed to update team connection, please try again'
              );
              setFormState({
                ...formState,
                loading: false
              });
            }
          );
        }
      }}
      isLoading={dataLoading}
      isNewModalStyle
      onClose={onClose}
      opened={isOpen}
      title="Rebase team connection"
    >
      {regTeam &&
        (formState.team ? (
          <AppStack>
            {alreadyImportedTeams.length > 0 && (
              <Alert color="yellow" variant="outline">
                <AppText weight={500}>
                  The following teams have already been imported to different
                  divisions;
                </AppText>
                <List withPadding>
                  {alreadyImportedTeams.map((t) => (
                    <List.Item key={t.pkRegAssociationDivisionTeam}>
                      {t.name} - {t.regAssociationDivision.name}
                    </List.Item>
                  ))}
                </List>
              </Alert>
            )}

            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to connect <b>{regTeam.name}</b> from
              registration to the <b>{formState.team.FullName}</b> website team?
            </AppText>

            {regTeam.team.name !== formState.team.FullName && (
              <AppText
                style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
              >
                Team name will be updated to match the website team name upon
                connection.
              </AppText>
            )}
          </AppStack>
        ) : (
          <AppStack>
            <Select
              clearable
              data={state.seasons.value
                .sort((a, b) => a.Name.localeCompare(b.Name))
                .map((s) => ({
                  value: s.ID.toString(),
                  label: s.Name
                }))}
              label="Season"
              onChange={(value) =>
                setFormState({
                  ...formState,
                  fkSeason: value
                })
              }
              placeholder="Filter by season"
              searchable
              value={formState.fkSeason ?? ''}
            />

            <AppStack style={{ gap: 10 }}>
              <AppFlexbox style={{ flex: 1, justifyContent: 'space-between' }}>
                <AppText weight={500}>Teams</AppText>
              </AppFlexbox>
              <TextInput
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    teamSearch: e.currentTarget.value,
                    pageIndex: 1
                  });
                }}
                placeholder="Search by name..."
                value={formState.teamSearch}
              />
              <AppStack style={{ gap: 0 }}>
                <PaginationList
                  emptyMessage="No teams available"
                  items={associationSeasonTeams.map((team) => {
                    const alreadyConnected =
                      regTeam.team?.team.pkTeam === team.App_ID;

                    return (
                      <AppFlexbox
                        key={team.ID}
                        className={
                          alreadyConnected ? null : classes['hover-action-card']
                        }
                        onClick={() => {
                          setFormState({
                            ...formState,
                            team
                          });
                        }}
                        style={{
                          padding: 10,
                          flex: 1,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: alreadyConnected
                            ? '#c8cbcf'
                            : '#FFF',
                          cursor: alreadyConnected ? 'default' : 'pointer'
                        }}
                      >
                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          {team.FullName}
                        </AppText>

                        {alreadyConnected ? (
                          <AppText
                            style={{
                              fontSize: 12,
                              color: '#4CAF50',
                              fontStyle: 'italic'
                            }}
                          >
                            Currently connected
                          </AppText>
                        ) : (
                          <ChevronRight color="#666" size={18} />
                        )}
                      </AppFlexbox>
                    );
                  })}
                  itemsPerPage={10}
                  onPageChange={(index) =>
                    setFormState({
                      ...formState,
                      pageIndex: index
                    })
                  }
                  pageIndex={formState.pageIndex}
                />
              </AppStack>
            </AppStack>
          </AppStack>
        ))}
    </ResponsiveModal>
  );
};

TeamRebaseConnectionModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  regAssociation: PropTypes.object,
  regTeam: PropTypes.object
};

export default TeamRebaseConnectionModal;
